// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the canvas stage. Draws the two cards and their symbols, owns the layout (which
// is also the hit-testing geometry — a stored circle per symbol, with the HIT radius
// expanded well past the drawn glyph so a target is ≥44px even where eight symbols share a
// card), and all the juice (particles, screen shake, a deal-in pop, correct/wrong flashes).
// It reads GameState; it never changes the rules. Layout is recomputed only when the pair
// changes or on resize, so a frame is cheap.

import { currentPair, type GameEvent, type GameState } from './game';
import { drawGlyph } from './glyphs';
import { accentFor, PALETTE } from './palette';

interface Placed {
  id: number;
  side: 0 | 1; // 0 = top card, 1 = bottom card
  x: number; // CSS px (pointer space)
  y: number;
  drawR: number;
  hitR: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
}

const TAU = Math.PI * 2;

export interface Slot {
  u: number; // unit x in [-1,1] relative to the card radius
  v: number;
  scale: number; // glyph draw radius relative to the card radius
}

/**
 * The unit layout for a card's `k` symbols: a centre glyph plus a ring of (k-1). Pure and
 * testable — layout.test.ts pins that no two glyphs' drawn circles overlap for every k the
 * modes use, which is the invariant a crowded-card layout bug would break (principle #20).
 * The renderer applies a per-card rotation offset and a tiny wobble on top; both are bounded
 * well inside the spacing margin this leaves.
 */
export function symbolLayout(k: number): Slot[] {
  const centerScale = k >= 8 ? 0.26 : 0.3;
  const ringR = k >= 8 ? 0.68 : 0.64;
  const ringScale = k >= 8 ? 0.235 : 0.28;
  const slots: Slot[] = [{ u: 0, v: 0, scale: centerScale }];
  for (let i = 1; i < k; i++) {
    const a = ((i - 1) / (k - 1)) * TAU;
    slots.push({ u: Math.cos(a) * ringR, v: Math.sin(a) * ringR, scale: ringScale });
  }
  return slots;
}

/** A cheap deterministic angle in [0, TAU) from three integers — stable across peers. */
function jitterAngle(a: number, b: number, c: number): number {
  const h = Math.sin(a * 12.9898 + b * 78.233 + c * 37.719) * 43758.5453;
  return (h - Math.floor(h)) * TAU;
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private w = 0; // CSS px
  private h = 0;
  private cardR = 0;
  private centers: Array<[number, number]> = [
    [0, 0],
    [0, 0],
  ];
  private placed: Placed[] = [];
  private lastIndex = -1;
  private lastSeed = -1;

  private particles: Particle[] = [];
  private shake = 0;
  private flash = 0; // >0 green, <0 red magnitude by sign
  private dealAnim = 0; // 0..1 fades to 0
  private highlight = -1; // keyboard cursor index into `placed`
  private t = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    if (w < 2 || h < 2) return; // ignore a transient 0-size measure
    this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    this.w = w;
    this.h = h;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.layoutCards();
    this.lastIndex = -1; // force a symbol relayout on the next frame
  }

  private layoutCards(): void {
    const { w, h } = this;
    const sideBySide = w > h * 1.15;
    if (sideBySide) {
      this.cardR = Math.min(w * 0.23, h * 0.44);
      const cy = h * 0.5;
      this.centers = [
        [w * 0.28, cy],
        [w * 0.72, cy],
      ];
    } else {
      this.cardR = Math.min(w * 0.46, h * 0.235);
      const cx = w * 0.5;
      this.centers = [
        [cx, h * 0.29],
        [cx, h * 0.72],
      ];
    }
  }

  /** Compute the on-screen circle for every symbol of the current pair. */
  private layoutSymbols(s: GameState): void {
    const { top, bottom } = currentPair(s);
    const cards: number[][] = [top, bottom];
    const R = this.cardR;
    const out: Placed[] = [];

    for (let side = 0 as 0 | 1; side < 2; side = (side + 1) as 0 | 1) {
      const card = cards[side];
      const k = card.length;
      const [cx, cy] = this.centers[side];
      const slots = symbolLayout(k);
      // A per-card rotation offset + a tiny radial wobble (both bounded well inside the
      // spacing margin symbolLayout leaves) so the two cards don't look mechanically identical.
      const rot = side ? 0.4 : 0;
      const positions: Array<[number, number, number, number]> = []; // x,y,drawR,id
      for (let i = 0; i < k; i++) {
        const slot = slots[i];
        let u = slot.u;
        let v = slot.v;
        if (i > 0) {
          const wob = 1 + (jitterAngle(s.seed + side * 7, i, s.index) / TAU - 0.5) * 0.04;
          const a = Math.atan2(v, u) + rot;
          const rr = Math.hypot(u, v) * wob;
          u = Math.cos(a) * rr;
          v = Math.sin(a) * rr;
        }
        positions.push([cx + u * R, cy + v * R, slot.scale * R, card[i]]);
      }
      // hit radius = expand toward the nearest neighbour so a target is comfortably ≥44px.
      for (let i = 0; i < positions.length; i++) {
        const [x, y, drawR, id] = positions[i];
        let nn = Infinity;
        for (let j = 0; j < positions.length; j++) {
          if (i === j) continue;
          const d = Math.hypot(positions[j][0] - x, positions[j][1] - y);
          if (d < nn) nn = d;
        }
        const hitR = Math.max(drawR, Math.min(nn * 0.47, R * 0.5));
        out.push({ id, side, x, y, drawR, hitR });
      }
    }
    this.placed = out;
    this.lastIndex = s.index;
    this.lastSeed = s.seed;
    this.dealAnim = 1;
  }

  onEvents(events: GameEvent[], reducedMotion: boolean): void {
    for (const e of events) {
      if (e.t === 'hit') {
        const p = this.placed.find((q) => q.id === e.symbol);
        this.flash = Math.max(this.flash, 0.7);
        if (p) this.burst(p.x, p.y, accentFor(e.symbol), reducedMotion ? 6 : 16, e.milestone ? 1.4 : 1);
      } else if (e.t === 'miss') {
        const p = this.placed.find((q) => q.id === e.symbol);
        this.flash = Math.min(this.flash, -1);
        if (!reducedMotion) this.shake = Math.max(this.shake, 12);
        if (p) this.burst(p.x, p.y, PALETTE.bad, reducedMotion ? 6 : 18, 1.1);
      }
    }
  }

  private burst(x: number, y: number, color: string, n: number, scale: number): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + Math.random() * 0.5;
      const sp = (1.2 + Math.random() * 2.6) * scale;
      this.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, max: 0.5 + Math.random() * 0.4, size: (2 + Math.random() * 3) * scale, color });
    }
  }

  setHighlight(i: number): void {
    if (!this.placed.length) return;
    const n = this.placed.length;
    this.highlight = ((i % n) + n) % n;
  }
  moveHighlight(delta: number): void {
    if (this.highlight < 0) this.highlight = 0;
    else this.setHighlight(this.highlight + delta);
  }
  highlightSymbol(): number | null {
    return this.highlight >= 0 && this.highlight < this.placed.length ? this.placed[this.highlight].id : null;
  }

  /** Viewport (client) coordinates of a placed symbol's centre — for the verification hook. */
  clientPosOf(id: number): { x: number; y: number } | null {
    const p = this.placed.find((q) => q.id === id);
    if (!p) return null;
    const rect = this.canvas.getBoundingClientRect();
    return { x: rect.left + p.x, y: rect.top + p.y };
  }

  /** Map a pointer position (CSS px, relative to the viewport) to a symbol id, or null. */
  hitTest(clientX: number, clientY: number): number | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best: Placed | null = null;
    let bestD = Infinity;
    for (const p of this.placed) {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d <= p.hitR && d < bestD) {
        best = p;
        bestD = d;
      }
    }
    return best ? best.id : null;
  }

  frame(s: GameState, opts: { dtMs: number; reducedMotion: boolean }): void {
    const dt = opts.dtMs;
    this.t += dt;
    if (s.index !== this.lastIndex || s.seed !== this.lastSeed) this.layoutSymbols(s);

    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // decay fx
    this.shake *= Math.pow(0.001, dt / 1000);
    if (this.shake < 0.4) this.shake = 0;
    this.flash *= Math.pow(0.002, dt / 1000);
    this.dealAnim *= Math.pow(0.0005, dt / 1000);
    if (this.dealAnim < 0.02) this.dealAnim = 0;

    // background
    const g = ctx.createRadialGradient(this.w / 2, this.h * 0.42, 10, this.w / 2, this.h * 0.5, Math.max(this.w, this.h) * 0.75);
    g.addColorStop(0, PALETTE.stage);
    g.addColorStop(1, PALETTE.stageEdge);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.w, this.h);

    const sx = this.shake ? (Math.random() - 0.5) * this.shake : 0;
    const sy = this.shake ? (Math.random() - 0.5) * this.shake : 0;
    ctx.save();
    ctx.translate(sx, sy);

    this.drawCard(0);
    this.drawCard(1);

    for (const p of this.placed) this.drawSymbol(p, s);

    // keyboard highlight ring
    if (this.highlight >= 0 && this.highlight < this.placed.length) {
      const p = this.placed[this.highlight];
      ctx.strokeStyle = PALETTE.spark;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.hitR, 0, TAU);
      ctx.stroke();
    }

    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const q = this.particles[i];
      q.x += q.vx * (dt / 16);
      q.y += q.vy * (dt / 16);
      q.vy += 0.06 * (dt / 16);
      q.life -= dt / 1000 / q.max;
      if (q.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = Math.max(0, q.life);
      ctx.fillStyle = q.color;
      ctx.beginPath();
      ctx.arc(q.x, q.y, q.size, 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // correct/wrong flash vignette
    if (Math.abs(this.flash) > 0.02) {
      const col = this.flash > 0 ? PALETTE.good : PALETTE.bad;
      const vg = ctx.createRadialGradient(this.w / 2, this.h / 2, Math.min(this.w, this.h) * 0.3, this.w / 2, this.h / 2, Math.max(this.w, this.h) * 0.62);
      vg.addColorStop(0, 'transparent');
      vg.addColorStop(1, col);
      ctx.globalAlpha = Math.min(0.5, Math.abs(this.flash) * 0.5);
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, this.w, this.h);
      ctx.globalAlpha = 1;
    }
  }

  private drawCard(side: 0 | 1): void {
    const ctx = this.ctx;
    const [cx, cy] = this.centers[side];
    const grow = side === 0 ? 1 + this.dealAnim * 0.04 : 1;
    const r = this.cardR * grow;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.4)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = PALETTE.card;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = PALETTE.cardEdge;
    ctx.lineWidth = Math.max(2, r * 0.02);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.stroke();
  }

  private drawSymbol(p: Placed, s: GameState): void {
    const ctx = this.ctx;
    const color = accentFor(p.id);
    const rot = jitterAngle(s.seed + p.side * 31, p.id, p.side);
    const grow = p.side === 0 ? 1 + this.dealAnim * 0.08 : 1;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(rot);
    ctx.scale(grow, grow);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    drawGlyph(ctx, p.id, p.drawR);
    ctx.restore();
  }

  destroy(): void {
    this.particles.length = 0;
  }
}
